import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  alpha
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Lightbulb as LightbulbIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Star as StarIcon,
  Info as InfoIcon
} from '@mui/icons-material';

interface ValuationData {
  methods: Record<string, ValuationMethod>;
  summary: ValuationSummary;
  assumptions: ValuationAssumptions;
  financialData: any;
  dataQuality: DataQuality;
}

interface ValuationMethod {
  name: string;
  value: number;
  confidence: 'high' | 'medium' | 'low';
  details: any;
  metadata?: {
    name: string;
    description: string;
    formula: string;
    bestFor: string[];
    limitations: string[];
    parameters: Record<string, any>;
  };
}

interface ValuationSummary {
  weightedValue: number;
  aiEnhancedValue: number;
  valueRange: { low: number; high: number };
  confidence: string;
  keyDrivers: string[];
  recommendations: string[];
}

interface ValuationAssumptions {
  capRate: { rate: number; noi: number };
  revenueMultiple: { multiple: number; revenue: number };
  dcf: { growthRate: number; discountRate: number; projectionYears: number };
  salesComparison: { pricePerRoom: number; numRooms: number };
  replacementCost: { costPerSqFt: number; depreciation: number; sqFt: number };
}

interface DataQuality {
  score: number;
  level: 'high' | 'medium' | 'low';
  missing: string[];
  available: string[];
}

interface ValuationSummaryPanelProps {
  data?: ValuationData | null;
  hotelName: string;
}

const ValuationSummaryPanel: React.FC<ValuationSummaryPanelProps> = ({
  data,
  hotelName
}) => {
  const theme = useTheme();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      notation: value >= 1000000 ? 'compact' : 'standard'
    }).format(value);
  };

  const formatDate = () => {
    return new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getConfidenceColor = (confidence: string) => {
    switch (confidence.toLowerCase()) {
      case 'high': return theme.palette.success.main;
      case 'medium': return theme.palette.warning.main;
      case 'low': return theme.palette.error.main;
      default: return theme.palette.text.secondary;
    }
  };

  const getValueRange = () => {
    if (!data?.methods) return { min: 0, max: 0, spread: 0 };

    const values = Object.values(data.methods)
      .filter(m => !m.name.includes("Weighted") && !m.name.includes("AI Enhanced"))
      .map(m => m.value);

    const min = Math.min(...values);
    const max = Math.max(...values);
    const spread = ((max - min) / max * 100);

    return { min, max, spread };
  };

  const getMethodsAnalysis = () => {
    if (!data?.methods) return { total: 0, high: 0, medium: 0, low: 0 };

    const methods = Object.values(data.methods)
      .filter(m => !m.name.includes("Weighted") && !m.name.includes("AI Enhanced"));

    return {
      total: methods.length,
      high: methods.filter(m => m.confidence === 'high').length,
      medium: methods.filter(m => m.confidence === 'medium').length,
      low: methods.filter(m => m.confidence === 'low').length
    };
  };

  const generateRecommendations = () => {
    if (!data) return [];

    const recommendations = [];
    const { spread } = getValueRange();
    const methodsAnalysis = getMethodsAnalysis();

    // Value spread analysis
    if (spread > 30) {
      recommendations.push("High variance in valuation methods suggests conducting additional market research to refine assumptions.");
    } else if (spread < 15) {
      recommendations.push("Strong consensus among valuation methods indicates reliable value estimate.");
    }

    // Data quality recommendations
    if (data.dataQuality?.level === 'low') {
      recommendations.push("Improve data quality by obtaining more comprehensive financial statements and operational metrics.");
    }

    // Method confidence recommendations
    if (methodsAnalysis.high === 0) {
      recommendations.push("Consider obtaining additional market data to improve confidence in valuation methods.");
    }

    // Default recommendations
    if (recommendations.length === 0) {
      recommendations.push("Valuation appears reasonable based on available data and market conditions.");
      recommendations.push("Consider periodic revaluation as market conditions change.");
    }

    return recommendations;
  };

  if (!data) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom fontWeight={600}>
          Valuation Summary
        </Typography>
        <Alert severity="info">
          No valuation data available. Please generate a valuation first.
        </Alert>
      </Box>
    );
  }

  const valueRange = getValueRange();
  const methodsAnalysis = getMethodsAnalysis();
  const recommendations = generateRecommendations();

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight={700}>
            Hotel Valuation Report
          </Typography>
          <Typography variant="h6" color="text.secondary">
            {hotelName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Report Date: {formatDate()}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<PrintIcon />} size="small">
            Print Report
          </Button>
          <Button variant="contained" startIcon={<DownloadIcon />} size="small">
            Export PDF
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Executive Summary */}
        <Grid item xs={12}>
          <Card elevation={2} sx={{ border: `2px solid ${theme.palette.primary.main}` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AssessmentIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h5" fontWeight={600} color="primary">
                  Executive Summary
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center', p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
                    <Typography variant="subtitle1" color="primary" gutterBottom>
                      Recommended Value
                    </Typography>
                    <Typography variant="h3" fontWeight={700} color="primary.main">
                      {formatCurrency(data.summary.aiEnhancedValue)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      AI Enhanced Valuation
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center', p: 2, backgroundColor: alpha(theme.palette.success.main, 0.05), borderRadius: 2 }}>
                    <Typography variant="subtitle1" color="success.main" gutterBottom>
                      Value Range
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {formatCurrency(data.summary.valueRange.low)} - {formatCurrency(data.summary.valueRange.high)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Conservative to Optimistic
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center', p: 2, backgroundColor: alpha(getConfidenceColor(data.summary.confidence), 0.05), borderRadius: 2 }}>
                    <Typography variant="subtitle1" color={getConfidenceColor(data.summary.confidence)} gutterBottom>
                      Confidence Level
                    </Typography>
                    <Chip
                      label={data.summary.confidence.toUpperCase()}
                      color={data.summary.confidence === 'high' ? 'success' : data.summary.confidence === 'medium' ? 'warning' : 'error'}
                      size="medium"
                      sx={{ fontSize: '1.1rem', fontWeight: 600 }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Overall Assessment
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Valuation Methods Summary */}
        <Grid item xs={12} md={8}>
          <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Valuation Methods Summary
              </Typography>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Method</TableCell>
                      <TableCell align="right">Value</TableCell>
                      <TableCell align="center">Weight</TableCell>
                      <TableCell align="center">Confidence</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(data.methods)
                      .filter(([key, method]) => !method.name.includes("Weighted") && !method.name.includes("AI Enhanced"))
                      .sort(([, a], [, b]) => b.value - a.value)
                      .map(([key, method]) => (
                        <TableRow key={key}>
                          <TableCell>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {method.name.replace(' Method', '')}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="subtitle1" fontWeight={600}>
                              {formatCurrency(method.value)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">
                              {((method.value / Object.values(data.methods)
                                .filter(m => !m.name.includes("Weighted") && !m.name.includes("AI Enhanced"))
                                .reduce((sum, m) => sum + m.value, 0)) * 100).toFixed(1)}%
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={method.confidence}
                              color={method.confidence === 'high' ? 'success' : method.confidence === 'medium' ? 'warning' : 'error'}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Key Statistics */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Key Statistics
              </Typography>

              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <StarIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Methods Used"
                    secondary={`${methodsAnalysis.total} valuation approaches`}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <TrendingUpIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Value Spread"
                    secondary={`${valueRange.spread.toFixed(1)}% variance between methods`}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color={data.dataQuality.level === 'high' ? 'success' : 'warning'} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Data Quality"
                    secondary={`${data.dataQuality.score}% - ${data.dataQuality.level.toUpperCase()}`}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <InfoIcon color="info" />
                  </ListItemIcon>
                  <ListItemText
                    primary="High Confidence Methods"
                    secondary={`${methodsAnalysis.high} of ${methodsAnalysis.total}`}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Key Value Drivers */}
        {data.summary.keyDrivers && data.summary.keyDrivers.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="h6" fontWeight={600}>
                    Key Value Drivers
                  </Typography>
                </Box>

                <List>
                  {data.summary.keyDrivers.map((driver, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CheckCircleIcon color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={driver} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Recommendations */}
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LightbulbIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight={600}>
                  Recommendations
                </Typography>
              </Box>

              <List>
                {recommendations.map((recommendation, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <LightbulbIcon color="warning" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={recommendation} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Data Quality Assessment */}
        <Grid item xs={12}>
          <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Data Quality Assessment
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="success.main" gutterBottom>
                    Available Data Sources
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {data.dataQuality.available.map((item, index) => (
                      <Chip key={index} label={item} size="small" color="success" variant="outlined" />
                    ))}
                  </Box>
                </Grid>

                {data.dataQuality.missing.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="warning.main" gutterBottom>
                      Missing Data Elements
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {data.dataQuality.missing.map((item, index) => (
                        <Chip key={index} label={item} size="small" color="warning" variant="outlined" />
                      ))}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Disclaimer */}
        <Grid item xs={12}>
          <Alert severity="info" variant="outlined">
            <Typography variant="body2">
              <strong>Disclaimer:</strong> This valuation is based on the financial data and assumptions provided.
              Market conditions, property-specific factors, and other variables may affect actual value.
              This analysis should be considered alongside professional appraisal and market analysis.
              The AI-enhanced valuation incorporates machine learning algorithms trained on market data,
              but should not be the sole basis for investment decisions.
            </Typography>
          </Alert>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ValuationSummaryPanel;